import {
  OktonEmptyState,
  OktonSearchFilter,
  OktonSelect,
} from '@okton/platform-ui';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Heading,
  Text,
  Button,
  Badge,
  useColorModeValue,
  Box,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { FaSync, FaEye } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import AnalyticsPage from '../../layout/IntegraAppPage';
import backend from '../../services/backend';
import { usePedidos } from '../../services/hooks/usePedidos';

type PedidoBruto = {
  id?: number | string;
  marketplace_order_id?: number | string;
  marketplace?: string;
  comprador_nome?: string;
  comprador_email?: string;
  data_pedido?: string;
  total?: number | string;
  status_label?: string;
  status_cor?: string;
};

type Pedido = {
  id: string;
  pedidoInternoId: number;
  interno: string;
  canal: string;
  cliente: string;
  email: string;
  data: string;
  valor: string;
  status: string;
  statusCor: string;
  bruto: PedidoBruto;
};

type PedidoRow = { row: { original: Pedido } };

const formatarData = (valor?: string) => {
  if (!valor) return '-';

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) return '-';

  return data.toLocaleDateString('pt-BR');
};

const formatarValor = (valor?: number | string) => {
  const numero = Number(valor ?? 0);

  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const mapearPedido = (pedido: PedidoBruto): Pedido => {
  return {
    id: String(pedido?.marketplace_order_id ?? pedido?.id ?? '-'),
    pedidoInternoId: Number(pedido?.id),
    interno: `Interno #${pedido?.id ?? '-'}`,
    canal:
      pedido?.marketplace === 'mercadolivre'
        ? 'Mercado Livre'
        : pedido?.marketplace || '-',
    cliente: pedido?.comprador_nome ?? '-',
    email: pedido?.comprador_email ?? '-',
    data: formatarData(pedido?.data_pedido),
    valor: formatarValor(pedido?.total),
    status: pedido?.status_label ?? '-',
    statusCor: pedido?.status_cor ?? 'gray',
    bruto: pedido,
  };
};

const Pedidos = () => {
  const history = useHistory();
  const toast = useToast();

  const bgPage = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('black', 'white');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [isAtualizando, setIsAtualizando] = useState(false);

  const {
    pedidos: pedidosBrutos,
    loading: carregando,
    erro,
    recarregar: carregarPedidos,
  } = usePedidos({
    status,
    limit: 50,
    offset: 0,
  });

  const pedidos = useMemo(() => {
    return pedidosBrutos.map(mapearPedido);
  }, [pedidosBrutos]);

  useEffect(() => {
    if (!erro) return;

    toast({
      title: erro,
      description: 'Não foi possível consultar os pedidos.',
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  }, [erro, toast]);

  const atualizarPedidos = async () => {
    try {
      setIsAtualizando(true);

      const response = await backend.post('/mercadolivre/pedidos/sincronizar', {
        status: status || undefined,
        limit: 50,
        offset: 0,
      });

      await carregarPedidos();

      toast({
        title: 'Pedidos atualizados',
        description: `${
          response.data?.pedidos_salvos ?? 0
        } pedido(s) sincronizado(s) e ${
          response.data?.estoques_processados ?? 0
        } estoque(s) processado(s).`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Erro ao atualizar pedidos',
        description: 'Não foi possível sincronizar os pedidos agora.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsAtualizando(false);
    }
  };

  const pedidosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return pedidos;

    return pedidos.filter(pedido => {
      return (
        pedido.id.toLowerCase().includes(termo) ||
        pedido.interno.toLowerCase().includes(termo) ||
        pedido.cliente.toLowerCase().includes(termo) ||
        pedido.email.toLowerCase().includes(termo)
      );
    });
  }, [pedidos, busca]);

  const colunasPedidos = useMemo(
    () => [
      {
        Header: 'PEDIDO',
        accessor: 'id',
        Cell: ({ row }: PedidoRow) => (
          <Flex flexDir="column">
            <Text color={textColor} fontWeight="600">
              {row.original.id}
            </Text>
            <Text fontSize="xs" color={secondaryTextColor}>
              {row.original.interno}
            </Text>
          </Flex>
        ),
      },
      {
        Header: 'CANAL',
        accessor: 'canal',
        Cell: ({ value }: { value: string }) => (
          <Badge colorScheme="yellow" variant="subtle">
            {value}
          </Badge>
        ),
      },
      {
        Header: 'CLIENTE',
        accessor: 'cliente',
        Cell: ({ row }: PedidoRow) => (
          <Flex flexDir="column">
            <Text color={textColor} fontWeight="500">
              {row.original.cliente}
            </Text>
            <Text fontSize="xs" color={secondaryTextColor}>
              {row.original.email}
            </Text>
          </Flex>
        ),
      },
      {
        Header: 'DATA',
        accessor: 'data',
      },
      {
        Header: 'VALOR',
        accessor: 'valor',
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ row }: PedidoRow) => (
          <Badge colorScheme={row.original.statusCor} variant="subtle">
            {row.original.status}
          </Badge>
        ),
      },
      {
        Header: 'AÇÕES',
        id: 'acoes',
        Cell: ({ row }: PedidoRow) => (
          <Flex
            align="center"
            color={textColor}
            justify="center"
            cursor="pointer"
            onClick={() =>
              history.push(`/pedidos/${row.original.pedidoInternoId}`)
            }
          >
            <FaEye />
          </Flex>
        ),
      },
    ],
    [textColor, secondaryTextColor, history],
  );

  return (
    <AnalyticsPage>
      <Flex justify="space-between" align="center" p={3}>
        <Heading fontWeight="bold" fontSize={25}>
          Pedidos
        </Heading>
      </Flex>

      <Flex
        justify="space-between"
        align="center"
        bg={bgPage}
        p={4}
        gap={4}
        flexDir="row"
        roundedTop="2xl"
        borderWidth="1px"
        borderBottomWidth={0}
      >
        <Flex w="100%">
          <OktonSearchFilter
            value={busca}
            onChange={setBusca}
            placeholder="Buscar por número ou cliente"
            maxWidth={400}
          />
        </Flex>

        <HStack spacing={2}>
          <OktonSelect
            name="status"
            value={status}
            onChange={event => setStatus(event.target.value)}
            options={[
              { value: '', label: 'Todos' },
              { value: '2', label: 'Pago' },
              { value: '3', label: 'Confirmado' },
              { value: '1', label: 'Pendente' },
              { value: '4', label: 'Cancelado' },
            ]}
          />

          <Button
            leftIcon={<FaSync color="white" />}
            bg="blue.500"
            borderRadius="md"
            isLoading={carregando || isAtualizando}
            onClick={atualizarPedidos}
          >
            <Text fontSize="15px" w="120px" color="white">
              Atualizar
            </Text>
          </Button>
        </HStack>
      </Flex>

      <Box bg={bgPage} roundedBottom="2xl" p="4" pt="0" borderWidth="1px">
        <Table columns={colunasPedidos} data={pedidosFiltrados} />

        {!carregando && pedidosFiltrados.length === 0 && (
          <OktonEmptyState
            title="Nenhum pedido encontrado"
            description="Não há pedidos para esta conta Mercado Livre com os filtros atuais."
          />
        )}
      </Box>
    </AnalyticsPage>
  );
};

export default Pedidos;
